import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';

/**
 * PDP: sincroniza entrega estimada al cambiar variante (morph desde fetch del variant-picker)
 * y al cambiar cantidad cuando el metacampo define stock > 0 y cabe superar ese tope.
 */
class NwProductEstimatedDelivery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.bundleMode === 'true') {
      const builder = this.closest('bundle-metaobject-builder');
      builder?.addEventListener('click', this.onBundleSelectionChange);
      builder?.addEventListener('change', this.onBundleSelectionChange);
      requestAnimationFrame(() => this.syncBundleSelection());
      return;
    }

    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateEstimatedDelivery);
    closestSection?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.onQuantitySelectorUpdate);
    requestAnimationFrame(() => this.syncLimitedStockPdpFromQuantity(this.readPdpQuantityFromForm()));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.dataset.bundleMode === 'true') {
      const builder = this.closest('bundle-metaobject-builder');
      builder?.removeEventListener('click', this.onBundleSelectionChange);
      builder?.removeEventListener('change', this.onBundleSelectionChange);
      return;
    }

    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateEstimatedDelivery);
    closestSection?.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.onQuantitySelectorUpdate);
  }

  onBundleSelectionChange = () => {
    requestAnimationFrame(() => this.syncBundleSelection());
  };

  /**
   * @param {VariantUpdateEvent} event
   */
  updateEstimatedDelivery = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.detail.data.productId &&
      event.detail.data.productId !== this.dataset.productId
    ) {
      return;
    }

    const newRoot = event.detail.data.html.querySelector(
      `nw-product-estimated-delivery[data-block-id="${this.dataset.blockId}"]`
    );

    if (newRoot instanceof HTMLElement) {
      morph(this, newRoot, { childrenOnly: true });
      this.syncLimitedStockPdpFromQuantity(this.readPdpQuantityFromForm());
    }

    this.syncProductBadges(event.detail.data.html);
  };

  /**
   * @param {Event} event
   */
  onQuantitySelectorUpdate = (event) => {
    const detail = /** @type {{ quantity?: number; cartLine?: number }} */ (event.detail);
    if (detail.cartLine) return;

    const section = this.closest('.shopify-section');
    const origin =
      event.target instanceof HTMLElement ? event.target.closest('.shopify-section') : null;
    if (!section || section !== origin) return;

    const qty = typeof detail.quantity === 'number' ? detail.quantity : this.readPdpQuantityFromForm();
    this.syncLimitedStockPdpFromQuantity(qty);
  };

  /**
   * @returns {number}
   */
  readPdpQuantityFromForm() {
    const section = this.closest('.shopify-section');
    const input = section?.querySelector('product-form-component input[name="quantity"]');
    const n = parseInt(String(input?.value ?? '1'), 10);
    return Number.isFinite(n) && n >= 1 ? n : 1;
  }

  /**
   * Pack: sin selección → «recíbelo mañana»; con selección → peor plazo (misma regla que el Liquid del snippet).
   */
  syncBundleSelection() {
    const builder = this.closest('bundle-metaobject-builder');
    if (!builder) return;

    const catalog = new Map();
    this.querySelectorAll('[data-nw-bundle-variant-catalog]').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const variantId = node.dataset.variantId;
      if (!variantId) return;
      catalog.set(String(variantId), {
        stockUnlimited: node.dataset.stockUnlimited === 'true',
        stockQty: parseInt(node.dataset.stockQty || '0', 10) || 0,
        supplierDays: parseInt(node.dataset.supplierDays || '0', 10) || 0,
      });
    });

    /** @type {{ quantity: number, variantId: string }[]} */
    const selected = [];
    builder.querySelectorAll('[data-bundle-line]').forEach((line) => {
      if (!(line instanceof HTMLElement)) return;
      const input = line.querySelector('[data-quantity-input]');
      const qty = parseInt(String(input?.value ?? '0'), 10);
      if (!Number.isFinite(qty) || qty <= 0) return;
      const variantId = line.dataset.variantId;
      if (!variantId) return;
      selected.push({ quantity: qty, variantId });
    });

    const isUkMarket = this.dataset.marketHandle === 'reino-unido';
    const baseMinDays = isUkMarket ? 5 : 1;
    const baseMaxDays = isUkMarket ? 6 : 2;

    if (selected.length === 0) {
      this.#showBundleDeliveryState(true, baseMinDays, baseMaxDays);
      return;
    }

    /** @type {{ inMainStock: boolean, minDays: number, maxDays: number }} */
    let worst = { inMainStock: true, minDays: baseMinDays, maxDays: baseMaxDays };

    for (const { quantity, variantId } of selected) {
      const entry = catalog.get(String(variantId));
      if (!entry) continue;

      let inMainStock = true;
      if (!entry.stockUnlimited) {
        if (entry.stockQty <= 0 || quantity > entry.stockQty) {
          inMainStock = false;
        }
      }

      let minDays = baseMinDays;
      let maxDays = baseMaxDays;
      if (!inMainStock) {
        minDays = baseMinDays + entry.supplierDays;
        maxDays = baseMaxDays + entry.supplierDays;
      }

      if (!inMainStock && (worst.inMainStock || maxDays > worst.maxDays)) {
        worst = { inMainStock, minDays, maxDays };
      }
    }

    this.#showBundleDeliveryState(worst.inMainStock, worst.minDays, worst.maxDays);
  }

  /**
   * @param {boolean} inMainStock
   * @param {number} minDays
   * @param {number} maxDays
   */
  #showBundleDeliveryState(inMainStock, minDays, maxDays) {
    const inStockEl = this.querySelector('[data-nw-est-state="in-stock"]');
    const extEl = this.querySelector('[data-nw-est-state="extended"]');
    const extHeadline = this.querySelector('[data-nw-est-extended-headline]');
    const extTooltip = extEl?.querySelector('.nw-tooltip__content');

    if (inMainStock) {
      inStockEl?.removeAttribute('hidden');
      extEl?.setAttribute('hidden', '');
      return;
    }

    inStockEl?.setAttribute('hidden', '');
    extEl?.removeAttribute('hidden');

    const fill = (tpl) =>
      (tpl || '').replaceAll('__MIN__', String(minDays)).replaceAll('__MAX__', String(maxDays));

    if (extHeadline instanceof HTMLElement) {
      extHeadline.innerHTML = fill(this.dataset.nwExtendedHeadlineTemplate);
      extHeadline.dataset.nwDeliveryFast = String(minDays >= 1 && maxDays <= 2);
    }
    if (extTooltip instanceof HTMLElement) {
      extTooltip.innerHTML = fill(this.dataset.nwExtendedTooltipTemplate);
    }
  }

  /**
   * @param {number} quantity
   */
  syncLimitedStockPdpFromQuantity(quantity) {
    const marker = this.querySelector('[data-nw-est-stock-qty]');
    if (!(marker instanceof HTMLElement)) return;

    const cap = parseInt(marker.dataset.nwEstStockQty || '', 10);
    if (!Number.isFinite(cap) || cap <= 0) return;

    const extended = quantity > cap;
    const inStockEl = this.querySelector('[data-nw-est-state="in-stock"]');
    const extEl = this.querySelector('[data-nw-est-state="extended"]');
    if (inStockEl instanceof HTMLElement) inStockEl.hidden = extended;
    if (extEl instanceof HTMLElement) extEl.hidden = !extended;

    // Delivery badge (rendered separately by nw-product-badges.liquid, in the sticky bar and/or the
    // PDP header): same `extended` decision, just toggling which pre-rendered badge twin is visible.
    const scope = this.closest('.shopify-section, dialog');
    scope?.querySelectorAll('[data-nw-est-badge-role]').forEach((badge) => {
      if (!(badge instanceof HTMLElement)) return;
      badge.hidden = extended ? badge.dataset.nwEstBadgeRole === 'in-stock' : badge.dataset.nwEstBadgeRole === 'extended';
    });
  }

  /**
   * Each badges root is rendered with a unique `context` (`pdp`, `sticky`, ...) turned into
   * `data-product-badges-root="<context>"` by `nw-product-badges.liquid`. Matching directly by that
   * attribute value avoids scanning the whole fetched document for ancestor classes and avoids
   * mismatches when there is more than one `[data-product-badges-root]` on the page (e.g. sticky bar
   * + PDP header + recommendation carousels).
   * @param {Document} newDocumentRoot
   */
  syncProductBadges(newDocumentRoot) {
    if (!newDocumentRoot) return;

    for (const currentBadgesRoot of document.querySelectorAll('[data-product-badges-root]')) {
      if (!(currentBadgesRoot instanceof HTMLElement)) continue;

      const context = currentBadgesRoot.dataset.productBadgesRoot;
      if (!context || context === 'default') continue;

      const newBadgesRoot = newDocumentRoot.querySelector(`[data-product-badges-root="${context}"]`);
      if (!(newBadgesRoot instanceof HTMLElement)) continue;

      morph(currentBadgesRoot, newBadgesRoot, { childrenOnly: true });
    }
  }
}

if (!customElements.get('nw-product-estimated-delivery')) {
  customElements.define('nw-product-estimated-delivery', NwProductEstimatedDelivery);
}
